import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { X, Save, User as UserIcon } from 'lucide-react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Session } from '@supabase/supabase-js';
import { registerCompleteSignup } from '@/services/LoyaltyService';

interface ProfileModalProps {
    session: Session;
    isOpen: boolean;
    onClose: () => void;
    onProfileUpdate: () => void;
}

export const ProfileModal = ({ session, isOpen, onClose, onProfileUpdate }: ProfileModalProps) => {
    const [displayName, setDisplayName] = useState('');
    const [birthdate, setBirthdate] = useState('');
    const [phone, setPhone] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const initialLoadRef = useRef(false);

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value.replace(/\D/g, '');
        if (!val) {
            setPhone('');
            return;
        }

        const hasPlus = e.target.value.includes('+');
        const isDdi = hasPlus || (val.startsWith('55') && val.length > 11);

        if (isDdi) {
            if (val.length > 13) val = val.slice(0, 13);
            const country = val.slice(0, 2);
            const ddd = val.slice(2, 4);
            const p1 = val.slice(4, 9);
            const p2 = val.slice(9, 13);

            let res = `+${country}`;
            if (ddd) res += ` (${ddd}`;
            if (p1) res += `) ${p1}`;
            if (p2) res += `-${p2}`;

            setPhone(res);
        } else {
            if (val.length > 11) val = val.slice(0, 11);
            const ddd = val.slice(0, 2);
            const p1 = val.slice(2, 7);
            const p2 = val.slice(7, 11);

            let res = '';
            if (ddd) res += `(${ddd}`;
            if (p1) res += `) ${p1}`;
            if (p2) res += `-${p2}`;

            setPhone(res);
        }
    };

    const email = session.user.email || '';
    const emailPrefix = email.split('@')[0];

    useEffect(() => {
        if (isOpen) {
            if (!initialLoadRef.current) {
                fetchProfile();
                initialLoadRef.current = true;
            }
        } else {
            initialLoadRef.current = false;
        }
    }, [isOpen]);

    const fetchProfile = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('display_name, birthdate, phone')
                .single();

            if (error && error.code !== 'PGRST116') { // Ignore not found error
                console.error('Error fetching profile:', error);
            }

            if (data) {
                setDisplayName(data.display_name || '');
                setBirthdate(data.birthdate || '');
                setPhone(data.phone || '');
            } else {
                setDisplayName('');
                setBirthdate('');
                setPhone('');
            }
        } catch (err) {
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        const nameToSave = displayName.trim() || emailPrefix;
        const phoneToSave = phone.trim() || null;
        const birthdateToSave = birthdate || null;

        try {
            const { error } = await supabase.rpc('update_my_profile', {
                p_display_name: nameToSave,
                p_birthdate: birthdateToSave,
                p_phone: phoneToSave,
            });

            if (error) throw error;

            if (nameToSave && phoneToSave && birthdateToSave) {
                await registerCompleteSignup();
            }

            onProfileUpdate(); // Notify parent to refresh
            onClose();
        } catch (err: any) {
            console.error('Error updating profile:', err);
            setError(err.message || 'Falha ao atualizar perfil. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-white/20 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 duration-200">

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/50">
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-full text-blue-600 dark:text-blue-400">
                            <UserIcon className="w-5 h-5" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Meu Perfil</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <form onSubmit={handleSave} className="p-6 space-y-5">

                    <div className="space-y-1.5">
                        <Input
                            label="Email"
                            name="email"
                            value={email}
                            disabled
                            className="bg-gray-100 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400 border-transparent cursor-not-allowed opacity-75"
                        />
                        <p className="text-[11px] text-gray-400 ml-1">O email não pode ser alterado.</p>
                    </div>

                    <div className="space-y-1.5">
                        <Input
                            label="Nome de Exibição"
                            name="displayName"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            placeholder={emailPrefix}
                            autoFocus
                        />
                        <p className="text-[11px] text-gray-400 ml-1 h-4">
                            Este nome será exibido no menu.
                        </p>
                    </div>

                    <div className="space-y-1.5">
                        <Input
                            label="Telefone"
                            name="phone"
                            type="tel"
                            value={phone}
                            onChange={handlePhoneChange}
                            placeholder="(11) 99999-9999"
                            maxLength={19}
                        />
                        <p className="text-[11px] text-gray-400 ml-1 h-4">
                            Este telefone será usado para contato em caso de necessidade.
                        </p>
                    </div>

                    <div className="space-y-1.5">
                        <Input
                            label="Nascimento"
                            name="birthdate"
                            type="date"
                            value={birthdate}
                            onChange={(e) => setBirthdate(e.target.value)}
                        />
                        <p className="text-[11px] text-gray-400 ml-1 h-4">
                            Esta data será usada para calcular seu bônus de aniversário.
                        </p>
                    </div>

                    {error && (
                        <div className="text-sm text-red-500 bg-red-50 dark:bg-red-900/10 p-3 rounded-xl border border-red-100 dark:border-red-900/20">
                            {error}
                        </div>
                    )}

                    <div className="pt-2 flex gap-3">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={onClose}
                            className="flex-1"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            isLoading={loading}
                            className="flex-1"
                        >
                            <Save className="w-4 h-4 mr-2" />
                            Salvar Alterações
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};
